import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  validateEmail,
  validatePhoneNumber,
} from "../../../Components/ReuseableFunctions/ValidationFunctions";
import SettingSearchLocationByInputStudent from "../../Institute/AddInstructor/Components/InstructorInfo/components/GooglePlacesApi/SettingSearchLocationByInputStudent";
import ImagePreviewerForEnrollmentLicense from "../../../Components/ImagePreviewer/ImagePreviewerForEnrollmentLicense";
import inVehicleOptions from "../../../JsonFiles/NewCourseInvehicleTrainingOptions.json";
import { BaseUrl } from "../../../Actions/Base";
import "../../CourseEnrollment/UserInfo/Components/Contact.css";

const fieldClass =
  "w-full h-[48px] rounded-[8px] border border-[#E8E8E8] px-4 text-[15px] text-[#404040] outline-none shadow-[0px_3px_6px_#00000029] bg-white";
const textareaClass =
  "w-full min-h-[120px] rounded-[8px] border border-[#E8E8E8] px-4 py-3 text-[15px] text-[#404040] outline-none shadow-[0px_3px_6px_#00000029] bg-white resize-y";
const labelClass = "text-[22px] text-[#404040] mb-2";

/** Validates license-type section selections before learner form can proceed. */
export function validateLicenseSelection(selectionState) {
  const { licenseType, sections } = selectionState || {};

  if (licenseType === "G1") {
    return null;
  }

  if (licenseType !== "G2" && licenseType !== "G") {
    return "Please select a license type.";
  }

  const { bde, inVehicle, driveTest } = sections || {};
  const anyChecked = bde?.checked || inVehicle?.checked || driveTest?.checked;

  if (!anyChecked) {
    return "Please select at least one service option (BDE, In-Vehicle, or Drive Test).";
  }

  if (bde?.checked && !bde?.packageId) {
    return "Please select a BDE package.";
  }

  if (inVehicle?.checked) {
    if (inVehicle.optionIndex === null || inVehicle.optionIndex === undefined) {
      return "Please select an In-Vehicle training option.";
    }

    const option = inVehicleOptions[inVehicle.optionIndex];
    if (option?.custom === true) {
      const hours = Number(inVehicle.customHours);
      if (!inVehicle.customHours || Number.isNaN(hours) || hours < 2) {
        return "Please enter In-Vehicle custom hours (minimum 2).";
      }
    }
  }

  if (driveTest?.checked) {
    if (driveTest.optionIndex === null || driveTest.optionIndex === undefined) {
      return "Please select a Drive Test vehicle option.";
    }
  }

  return null;
}

function hasFullNameWithSpace(name) {
  const trimmed = (name || "").trim();
  return trimmed.includes(" ") && trimmed.split(/\s+/).length >= 2;
}

function getTomorrowDateString() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
}

function isFutureDate(dateStr) {
  if (!dateStr) return false;
  const selected = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  selected.setHours(0, 0, 0, 0);
  return selected > today;
}

function isSelectionErrorMessage(message) {
  if (!message) return false;
  return (
    message.includes("service option") ||
    message.includes("BDE package") ||
    message.includes("In-Vehicle") ||
    message.includes("Drive Test") ||
    message.includes("license type")
  );
}

const LearnerInformation = forwardRef(function LearnerInformation(
  { selectionState, learnerInfo, setLearnerInfo },
  ref
) {
  const { UserInfo, Authenticated } = useSelector(
    (store) => store.LoginSignupReducer
  );

  const [error, setError] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [coords, setCoords] = useState(null);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadSubmitSuccess, setLeadSubmitSuccess] = useState(false);
  const hideTimerRef = useRef(null);
  const repeatTimerRef = useRef(null);
  const errorRef = useRef("");

  useEffect(() => {
    errorRef.current = error;
  }, [error]);

  const anyServiceSelected = useMemo(() => {
    const { bde, inVehicle, driveTest } = selectionState?.sections || {};
    return Boolean(bde?.checked || inVehicle?.checked || driveTest?.checked);
  }, [selectionState]);

  // Lead-only: none of the 3 service checkboxes selected
  const isLeadOnlyMode = !anyServiceSelected;

  const loggedInEmail = useMemo(() => {
    if (!Authenticated || !UserInfo) return "";
    return UserInfo?.Email || UserInfo?.StudentInfo?.Email || "";
  }, [Authenticated, UserInfo]);

  const emailReadOnly = Boolean(loggedInEmail);

  useEffect(() => {
    if (!loggedInEmail) return;
    setLearnerInfo((prev) => ({
      ...prev,
      email: loggedInEmail,
      confirmEmail: loggedInEmail,
    }));
  }, [loggedInEmail, setLearnerInfo]);

  useEffect(() => {
    clearTimeout(hideTimerRef.current);
    clearTimeout(repeatTimerRef.current);

    if (!error) {
      setShowErrorToast(false);
      return;
    }

    const startCycle = () => {
      if (!errorRef.current) {
        setShowErrorToast(false);
        return;
      }

      setShowErrorToast(true);
      hideTimerRef.current = setTimeout(() => {
        setShowErrorToast(false);
        repeatTimerRef.current = setTimeout(() => {
          startCycle();
        }, 25000);
      }, 5000);
    };

    startCycle();

    return () => {
      clearTimeout(hideTimerRef.current);
      clearTimeout(repeatTimerRef.current);
    };
  }, [error]);

  // Clear stale selection errors when mode / selection changes
  useEffect(() => {
    setError((prev) => (isSelectionErrorMessage(prev) ? "" : prev));
    if (!isLeadOnlyMode) setLeadSubmitSuccess(false);
  }, [selectionState, isLeadOnlyMode]);

  const setField = (field, value) => {
    setLearnerInfo((prev) => ({ ...prev, [field]: value }));
  };

  const showFieldError = (message) => {
    setError(message);
  };

  const clearFieldErrorIfMatch = (messages) => {
    setError((prev) => (messages.includes(prev) ? "" : prev));
  };

  const handleBlurFullName = (e) => {
    if (isLeadOnlyMode) return;
    const value = e.target.value;
    if (!value?.trim()) {
      showFieldError("Full name is required.");
      return;
    }
    if (!hasFullNameWithSpace(value)) {
      showFieldError(
        "Full name must include first and last name separated by a space."
      );
      return;
    }
    clearFieldErrorIfMatch([
      "Full name is required.",
      "Full name must include first and last name separated by a space.",
    ]);
  };

  const handleBlurEmail = (e) => {
    if (emailReadOnly) return;
    const value = e.target.value;

    // Lead-only: only validate format when a value is entered
    if (isLeadOnlyMode) {
      if (!value?.trim()) {
        clearFieldErrorIfMatch([
          "Email address is required.",
          "Please enter a valid email address.",
          "Please fill in either email or phone number.",
        ]);
        return;
      }
      if (!validateEmail(value)) {
        showFieldError("Please enter a valid email address.");
        return;
      }
      clearFieldErrorIfMatch([
        "Please enter a valid email address.",
        "Please fill in either email or phone number.",
      ]);
      return;
    }

    if (!value?.trim()) {
      showFieldError("Email address is required.");
      return;
    }
    if (!validateEmail(value)) {
      showFieldError("Please enter a valid email address.");
      return;
    }
    clearFieldErrorIfMatch([
      "Email address is required.",
      "Please enter a valid email address.",
    ]);
  };

  const handleBlurConfirmEmail = (e) => {
    if (emailReadOnly) return;
    const value = e.target.value;

    if (isLeadOnlyMode) {
      if (!value?.trim()) {
        clearFieldErrorIfMatch([
          "Confirm email is required.",
          "Please enter a valid confirm email address.",
          "Email and Confirm Email must match.",
        ]);
        return;
      }
      if (!validateEmail(value)) {
        showFieldError("Please enter a valid confirm email address.");
        return;
      }
      if (learnerInfo.email && learnerInfo.email !== value) {
        showFieldError("Email and Confirm Email must match.");
        return;
      }
      clearFieldErrorIfMatch([
        "Please enter a valid confirm email address.",
        "Email and Confirm Email must match.",
      ]);
      return;
    }

    if (!value?.trim()) {
      showFieldError("Confirm email is required.");
      return;
    }
    if (!validateEmail(value)) {
      showFieldError("Please enter a valid confirm email address.");
      return;
    }
    if (learnerInfo.email !== value) {
      showFieldError("Email and Confirm Email must match.");
      return;
    }
    clearFieldErrorIfMatch([
      "Confirm email is required.",
      "Please enter a valid confirm email address.",
      "Email and Confirm Email must match.",
    ]);
  };

  const handleBlurPhone = (e) => {
    const value = e?.target?.value ?? learnerInfo.phone;

    if (isLeadOnlyMode) {
      if (!value?.trim()) {
        clearFieldErrorIfMatch([
          "Phone number is required.",
          "Please enter a valid phone number.",
          "Please fill in either email or phone number.",
        ]);
        return;
      }
      if (!validatePhoneNumber(value)) {
        showFieldError("Please enter a valid phone number.");
        return;
      }
      clearFieldErrorIfMatch([
        "Please enter a valid phone number.",
        "Please fill in either email or phone number.",
      ]);
      return;
    }

    if (!value?.trim()) {
      showFieldError("Phone number is required.");
      return;
    }
    if (!validatePhoneNumber(value)) {
      showFieldError("Please enter a valid phone number.");
      return;
    }
    clearFieldErrorIfMatch([
      "Phone number is required.",
      "Please enter a valid phone number.",
    ]);
  };

  const handleBlurStartDate = (e) => {
    if (isLeadOnlyMode) return;
    const value = e.target.value;
    if (!value) {
      showFieldError("Preferred start date is required.");
      return;
    }
    if (!isFutureDate(value)) {
      showFieldError("Preferred start date must be a future date.");
      return;
    }
    clearFieldErrorIfMatch([
      "Preferred start date is required.",
      "Preferred start date must be a future date.",
    ]);
  };

  const handleBlurAddress = () => {
    if (isLeadOnlyMode) return;
    if (!learnerInfo.address?.trim()) {
      showFieldError("Please enter your full address.");
      return;
    }
    clearFieldErrorIfMatch(["Please enter your full address."]);
  };

  const handleChange = (field) => (e) => {
    setField(field, e.target.value);
  };

  const handlePhoneChange = (value) => {
    setField("phone", value);
  };

  const handleAddressData = (updater) => {
    setLearnerInfo((prev) => {
      const next =
        typeof updater === "function"
          ? updater({ Address: prev.address })
          : updater;
      return {
        ...prev,
        address: next?.Address ?? prev.address,
      };
    });
  };

  const handleLicenseUpload = (side) => (e) => {
    const file = e.target.files?.[0] || null;
    setField(side === "front" ? "licenseFront" : "licenseBack", file);
  };

  /** Lead-only submit: require email OR phone; validate whichever is filled. */
  const validateLeadOnlySubmit = () => {
    const email = (learnerInfo.email || "").trim();
    // PhoneInput often keeps dial code only (e.g. "1") when empty — treat as empty
    const phoneRaw = (learnerInfo.phone || "").trim();
    const phoneDigits = phoneRaw.replace(/\D/g, "");
    const phone = phoneDigits.length > 3 ? phoneRaw : "";
    const confirmEmail = (learnerInfo.confirmEmail || "").trim();

    if (!email && !phone) {
      setError("Please fill in either email or phone number.");
      return false;
    }

    if (email) {
      if (!validateEmail(email)) {
        setError("Please enter a valid email address.");
        return false;
      }
      if (!emailReadOnly) {
        if (confirmEmail && !validateEmail(confirmEmail)) {
          setError("Please enter a valid confirm email address.");
          return false;
        }
        if (confirmEmail && email !== confirmEmail) {
          setError("Email and Confirm Email must match.");
          return false;
        }
      }
    }

    if (phone && !validatePhoneNumber(phone)) {
      setError("Please enter a valid phone number.");
      return false;
    }

    setError("");
    return true;
  };

  const handleLeadOnlySubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingLead) return;
    if (!validateLeadOnlySubmit()) return;

    setIsSubmittingLead(true);
    setLeadSubmitSuccess(false);

    try {
      const axiosInstance = axios.create({
        baseURL: BaseUrl,
        withCredentials: true,
      });

      const email = (learnerInfo.email || "").trim();
      const phoneRaw = (learnerInfo.phone || "").trim();
      const phoneDigits = phoneRaw.replace(/\D/g, "");
      const phone = phoneDigits.length > 3 ? phoneRaw : "";

      const payload = {
        fullName: (learnerInfo.fullName || "").trim(),
        email,
        phone,
        website: learnerInfo.website || "",
      };

      // Honeypot filled → pretend success, do not hit API
      if (String(payload.website || "").trim()) {
        setError("");
        setLeadSubmitSuccess(true);
        return;
      }

      console.log("📧 Register course lead — emails BEFORE API call:");
      console.log("   → Admin (headoffice): headoffice@vehiclelearning.com");
      console.log("   → Admin (hijab.zahra): hijab.zahra@vehiclelearning.com");
      console.log(
        "   → User thank-you:",
        email || "(skipped — no email provided)"
      );
      console.log("   → Payload:", { ...payload, website: "[redacted]" });

      await axiosInstance.post("/api/register-course-lead", payload);

      setError("");
      setLeadSubmitSuccess(true);
    } catch (err) {
      const apiMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to submit your information. Please try again.";
      setError(apiMessage);
      setLeadSubmitSuccess(false);
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const validateLearnerForm = () => {
    if (isLeadOnlyMode) {
      return validateLeadOnlySubmit();
    }

    const selectionError = validateLicenseSelection(selectionState);
    if (selectionError) {
      setError(selectionError);
      return false;
    }

    if (!hasFullNameWithSpace(learnerInfo.fullName)) {
      setError(
        "Full name must include first and last name separated by a space."
      );
      return false;
    }

    if (!validateEmail(learnerInfo.email)) {
      setError("Please enter a valid email address.");
      return false;
    }

    if (!validateEmail(learnerInfo.confirmEmail)) {
      setError("Please enter a valid confirm email address.");
      return false;
    }

    if (!emailReadOnly && learnerInfo.email !== learnerInfo.confirmEmail) {
      setError("Email and Confirm Email must match.");
      return false;
    }

    if (!validatePhoneNumber(learnerInfo.phone)) {
      setError("Please enter a valid phone number.");
      return false;
    }

    if (!learnerInfo.address?.trim()) {
      setError("Please enter your full address.");
      return false;
    }

    if (!isFutureDate(learnerInfo.startDate)) {
      setError("Preferred start date must be a future date.");
      return false;
    }

    if (!learnerInfo.licenseFront) {
      setError("Please attach the front of your latest license/permit.");
      return false;
    }

    if (!learnerInfo.licenseBack) {
      setError("Please attach the back of your latest license/permit.");
      return false;
    }

    setError("");
    return true;
  };

  useImperativeHandle(ref, () => ({
    validate: validateLearnerForm,
    getCoords: () => coords,
  }));

  const tomorrow = getTomorrowDateString();

  return (
    <div className="w-full flex justify-center px-4 py-10">
      {showErrorToast && error && (
        <div
          className="fixed flex items-center gap-[10px] bottom-[20px] right-[80px] z-[9999] px-6 py-2 rounded-lg
            bg-[linear-gradient(90deg,_#FFFFFF_0%,_#DFE0FF_28%,_#D7D9FF_100%)] bg-no-repeat text-[#F93D5C] text-[16px] shadow-[0_0_8px_#00000029]
            max-w-[450px] whitespace-pre-wrap break-words border border-[#DCDCDC] animate-fade-in-out"
        >
          <img
            className="h-6 relative -top-[2px] md:top-0 md:h-auto"
            src={require("../../CourseEnrollment/Assets/ErrorIcon.svg").default}
            alt="Form validation error"
          />
          {error}
        </div>
      )}

      <div className="w-[100%] lg:w-[70%] rounded-[10px] bg-white p-6 md:p-8">
        {/* Honeypot — hidden from users; bots that autofill forms often populate it */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
            opacity: 0,
          }}
        >
          <label htmlFor="website">Website</label>
          <input
            id="website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={learnerInfo.website || ""}
            onChange={handleChange("website")}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
          {/*
            DOM order = mobile sequence (Full Name → Email → Confirm Email → …).
            md:col/row placement keeps the original 2-column desktop layout.
          */}
          <div className="flex flex-col md:col-start-1 md:row-start-1">
            <label className={labelClass} htmlFor="fullName">
              Full Name
            </label>
            <input
              id="fullName"
              type="text"
              placeholder="John Doe"
              className={fieldClass}
              value={learnerInfo.fullName}
              onChange={handleChange("fullName")}
              onBlur={handleBlurFullName}
            />
          </div>

          <div className="flex flex-col md:col-start-1 md:row-start-2">
            <label className={labelClass} htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              placeholder="Enter Email"
              className={`${fieldClass} ${
                emailReadOnly ? "bg-[#F5F5F5] cursor-not-allowed" : ""
              }`}
              value={learnerInfo.email}
              onChange={handleChange("email")}
              onBlur={handleBlurEmail}
              readOnly={emailReadOnly}
            />
          </div>

          <div className="flex flex-col md:col-start-2 md:row-start-2">
            <label className={labelClass} htmlFor="confirmEmail">
              Confirm Email Address
            </label>
            <input
              id="confirmEmail"
              type="email"
              placeholder="Enter Email"
              className={`${fieldClass} ${
                emailReadOnly ? "bg-[#F5F5F5] cursor-not-allowed" : ""
              }`}
              value={learnerInfo.confirmEmail}
              onChange={handleChange("confirmEmail")}
              onBlur={handleBlurConfirmEmail}
              readOnly={emailReadOnly}
            />
          </div>

          <div className="flex flex-col md:col-start-2 md:row-start-1">
            <label className={labelClass} htmlFor="phone">
              Phone
            </label>
            <PhoneInput
              country="ca"
              value={learnerInfo.phone}
              onChange={handlePhoneChange}
              inputProps={{
                id: "phone",
                name: "phone",
                onBlur: handleBlurPhone,
              }}
              containerClass="PhoneNumberInput"
              inputClass="CustomPhoneNumberInput"
              containerStyle={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "48px",
                borderRadius: "8px",
                border: "1px solid #E8E8E8",
                boxShadow: "0px 3px 6px #00000029",
              }}
            />
          </div>

          <div className="flex flex-col md:col-start-1 md:row-start-3">
            <label className={labelClass} htmlFor="Address">
              Address
            </label>
            <div onBlur={handleBlurAddress}>
              <SettingSearchLocationByInputStudent
                InstructorData={{ Address: learnerInfo.address }}
                setInstructorData={handleAddressData}
                setSelectedCoordinates={setCoords}
                setErr={() => {}}
                newCountryPostalCode="Canada"
                EnrollmentData={{ StudentData: { Country: "Canada" } }}
                Id="Address"
                addressType="Address"
              />
            </div>
          </div>

          <div className="flex flex-col md:col-start-2 md:row-start-3">
            <label className={labelClass} htmlFor="startDate">
              Start Date (Preferred)
            </label>
            <input
              id="startDate"
              type="date"
              min={tomorrow}
              className={fieldClass}
              value={learnerInfo.startDate}
              onChange={handleChange("startDate")}
              onBlur={handleBlurStartDate}
            />
          </div>

          <div className="flex flex-col md:col-start-1 md:row-start-4">
            <label className={labelClass} htmlFor="notes">
              Notes (Optional)
            </label>
            <textarea
              id="notes"
              placeholder="Message..."
              className={textareaClass}
              value={learnerInfo.notes}
              onChange={handleChange("notes")}
            />
          </div>

          <div className="flex flex-col md:col-start-2 md:row-start-4">
            <label className={labelClass} htmlFor="guardianContact">
              Guardian Phone Number/Email Address (Optional)
            </label>
            <textarea
              id="guardianContact"
              placeholder="Description..."
              className={textareaClass}
              value={learnerInfo.guardianContact}
              onChange={handleChange("guardianContact")}
            />
          </div>

          <div className="flex flex-col gap-3 md:col-start-1 md:row-start-5">
            <p className="text-[22px] text-[#404040]">
              Attach Your Latest License/Permit
            </p>
            <div className="flex flex-wrap gap-8">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-[15px] font-semibold text-[#000] mb-1">
                    Front
                  </p>
                  <ImagePreviewerForEnrollmentLicense
                    Id="LicenseFront"
                    OnChange={handleLicenseUpload("front")}
                    ImgUrl={learnerInfo.licenseFront}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div>
                  <p className="text-[15px] font-semibold text-[#000] mb-1">
                    Back
                  </p>
                  <ImagePreviewerForEnrollmentLicense
                    Id="LicenseBack"
                    OnChange={handleLicenseUpload("back")}
                    ImgUrl={learnerInfo.licenseBack}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {isLeadOnlyMode && (
          <div className="w-full flex flex-col items-end gap-3 mt-8">
            {leadSubmitSuccess && (
              <p className="text-[16px] text-[#2E7D32] text-right">
                Thank you! Your information has been received successfully.
              </p>
            )}
            <button
              type="button"
              onClick={handleLeadOnlySubmit}
              disabled={isSubmittingLead}
              className={`min-w-[160px] h-[44px] rounded-[8px] border-none text-white text-[18px] ${
                isSubmittingLead
                  ? "bg-[#A1A3EF] opacity-60 cursor-not-allowed"
                  : "bg-[#A1A3EF] cursor-pointer"
              }`}
            >
              {isSubmittingLead ? "Submitting..." : "Submit"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default LearnerInformation;
